import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../../firebase-config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../assets/styles/ClientShared.css";

const initialFormData = {
  businessName: "",
  businessSummary: "",
  bestServices: "",
  leadServices: "",
  serviceArea: "",
  idealCustomer: "",
  customerProblems: "",
  buyerObjections: "",
  proof: "",
  competitors: "",
  differentiators: "",
  currentWebsite: "",
  pagesNeeded: "",
  primaryCTA: "",
  goodLead: "",
  badLead: "",
  preferredColors: "",
  brandStyle: "",
  referenceSites: "",
  mustHaveContent: "",
};

const sections = [
  {
    title: "Business & Offer",
    description: "This helps shape the messaging and service-page structure.",
    fields: [
      { name: "businessName", label: "Business name", type: "input", required: true },
      { name: "businessSummary", label: "What does the business do? Keep it clear and practical.", type: "textarea", required: true },
      { name: "bestServices", label: "Which services/products make you the most money?", type: "textarea", required: true },
      { name: "leadServices", label: "Which services/products do you want more leads for?", type: "textarea", required: true },
      { name: "serviceArea", label: "What cities, neighborhoods, or areas do you serve?", type: "textarea" },
    ],
  },
  {
    title: "Customers & Positioning",
    description: "This helps the site speak to the right buyer instead of sounding generic.",
    fields: [
      { name: "idealCustomer", label: "Who is your ideal customer?", type: "textarea" },
      { name: "customerProblems", label: "What problems are customers usually trying to solve when they contact you?", type: "textarea" },
      { name: "buyerObjections", label: "What questions or objections do customers usually have before buying?", type: "textarea" },
      { name: "proof", label: "What proof do you have? Reviews, photos, certifications, years in business, results, etc.", type: "textarea" },
      { name: "competitors", label: "Who are your main competitors?", type: "textarea" },
      { name: "differentiators", label: "Why should a customer choose you instead of them?", type: "textarea" },
    ],
  },
  {
    title: "Website Direction",
    description: "This keeps the custom design focused on leads and clarity.",
    fields: [
      { name: "currentWebsite", label: "Current website or social links", type: "textarea" },
      { name: "pagesNeeded", label: "What pages do you think the site needs?", type: "textarea", placeholder: "Example: Home, About, Services, Gallery, FAQ, Contact" },
      { name: "primaryCTA", label: "What is the main action visitors should take?", type: "input", placeholder: "Example: request a quote, book a call, call now" },
      { name: "goodLead", label: "What makes someone a good lead?", type: "textarea" },
      { name: "badLead", label: "What makes someone a bad fit?", type: "textarea" },
    ],
  },
  {
    title: "Design Direction",
    description: "Enough direction to design confidently without dragging the process out.",
    fields: [
      { name: "preferredColors", label: "Preferred colors", type: "input" },
      { name: "brandStyle", label: "What style should the site feel like?", type: "textarea", placeholder: "Example: premium, clean, bold, friendly, modern, local, professional" },
      { name: "referenceSites", label: "Reference websites you like", type: "textarea" },
      { name: "mustHaveContent", label: "Anything that absolutely must be included?", type: "textarea" },
    ],
  },
];

const GrowthIntake = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("User not authenticated.");

        const userSnap = await getDoc(doc(db, "users", currentUser.uid));
        if (!userSnap.exists()) throw new Error("User document not found.");

        const userData = userSnap.data();
        if (!userData.projectId) throw new Error("Project ID not found.");

        setProjectId(userData.projectId);
        const projectSnap = await getDoc(doc(db, "projects", userData.projectId));
        if (projectSnap.exists()) {
          setFormData({ ...initialFormData, ...(projectSnap.data().growthIntake || {}) });
        }
      } catch (err) {
        setError(err.message);
        toast.error("Failed to load Growth intake.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.businessName || !formData.businessSummary || !formData.bestServices || !formData.leadServices) {
      setError("Please complete the required business and offer fields before saving.");
      toast.warn("Please complete the required fields.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      if (!projectId) throw new Error("Project ID not set.");
      await updateDoc(doc(db, "projects", projectId), {
        growthIntake: formData,
      });
      toast.success("Growth intake saved.");
    } catch (err) {
      setError("Failed to save intake: " + err.message);
      toast.error("Failed to save Growth intake.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="cp-loading"><p>Loading Growth intake...</p></div>;
  }

  return (
    <div className="cp-page">
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">Growth Intake</h1>
        <p className="cp-subtitle">
          This replaces generic page-by-page homework. Answer these questions so we can shape your messaging, sitemap, and lead flow.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {sections.map((section) => (
          <div key={section.title} className="cp-section" style={{ marginBottom: "20px" }}>
            <div className="cp-section-header">
              <div>
                <h2 className="cp-section-title">{section.title}</h2>
                <p className="cp-empty-text" style={{ marginTop: "4px" }}>{section.description}</p>
              </div>
            </div>
            <div className="cp-section-body">
              {section.fields.map((field) => (
                <div key={field.name} className="cp-form-group">
                  <label>
                    {field.label}
                    {field.required ? " *" : ""}
                  </label>
                  {field.type === "input" ? (
                    <input
                      type="text"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder || ""}
                      required={field.required}
                    />
                  ) : (
                    <textarea
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder || ""}
                      required={field.required}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {error && <p className="cp-message--error">{error}</p>}

        <div className="cp-btn-row">
          <button type="submit" className="cp-btn-primary" disabled={saving || !projectId}>
            {saving ? "Saving..." : "Save Growth Intake"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GrowthIntake;
